// pages/AdminPage.js
// Admin panel: manage periods and the Client Groups ingest console.
//
// Periods now support CONCURRENT active cycles, each gated by staff office
// and/or department, plus per-period section visibility. Activating a period
// no longer closes the others.

import { useEffect, useState, useCallback, useMemo, useRef, Fragment } from 'react';
import TopBar from '../components/TopBar';
import { useAuth } from '../auth/AuthContext';
import ClientGroupsAdminPanel from './ClientGroupsAdminPanel';
import NavbotAdminPanel from './NavbotAdminPanel';
import DataRefreshAdminPanel from './DataRefreshAdminPanel';
import PeApplyAdminPanel from './PeApplyAdminPanel';
import PhasesEditor from './PhasesEditor';
import ToggleSwitch from './sections/ToggleSwitch';
import { augmentsFor, augmentOn } from './sectionAugments';
import './AdminPage.css';

// Gate columns are JSON-array strings (or null). Parse defensively.
function parseList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a : []; }
  catch { return String(raw).split(',').map(s => s.trim()).filter(Boolean); }
}

function gateSummary(p) {
  const o = parseList(p.gate_office_codes);
  const d = parseList(p.gate_department_names);
  if (!o.length && !d.length) return 'Everyone';
  const parts = [];
  if (o.length) parts.push(`Offices: ${o.join(', ')}`);
  if (d.length) parts.push(`Depts: ${d.join(', ')}`);
  return parts.join(' · ');
}

export default function AdminPage() {
  // Re-read the app's periods + visible sections after any change here, so Home,
  // the TopBar and section routes reflect it without a page reload.
  const { reload: reloadAppConfig } = useAuth();
  const [periods, setPeriods]   = useState([]);
  const [sections, setSections] = useState([]);
  const [dims, setDims]         = useState({ offices: [], departments: [] });
  const [saving,  setSaving]    = useState(false);
  const [msg,     setMsg]       = useState(null);
  const [tab,     setTab]       = useState('periods');
  const [editId,  setEditId]    = useState(null);   // period id being configured
  const [showCreate, setShowCreate] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);   // periods + sections first load

  const [newPeriod, setNewPeriod] = useState({
    period: '', label: '', description: '', start_date: '', end_date: '',
    gate_office_codes: [], gate_department_names: [],
  });

  const fetchPeriods = useCallback(async () => {
    const res = await fetch('/api/diAdmin/periods', { credentials: 'include' });
    if (res.ok) setPeriods(await res.json());
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchPeriods(),
      fetch('/api/diAdmin/sections', { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(list => { if (!cancelled) setSections((list || []).filter(s => s.isActive)); }),
      fetch('/api/diAdmin/staff-dimensions', { credentials: 'include' })
        .then(r => r.ok ? r.json() : { offices: [], departments: [] })
        .then(d => { if (!cancelled) setDims(d); }),
    ]).catch(() => {}).finally(() => { if (!cancelled) setInitialLoading(false); });
    return () => { cancelled = true; };
  }, [fetchPeriods]);

  // Errors stay up long enough to read; a newer message resets the timer.
  const flashTimer = useRef(null);
  function flash(text, isError = false) {
    setMsg({ text, isError });
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setMsg(null), isError ? 10000 : 3000);
  }

  async function togglePeriod(p) {
    setSaving(true);
    try {
      const res = await fetch(`/api/diAdmin/periods/${p.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !p.is_active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flash(p.is_active ? 'Period closed.' : 'Period activated.');
      reloadAppConfig({ silent: true });
      await fetchPeriods();
    } catch (err) { flash(err.message, true); }
    finally { setSaving(false); }
  }

  function toggleInArray(arr, val) {
    return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
  }

  async function createPeriod() {
    if (!newPeriod.period || !newPeriod.label) return flash('Period code and label are required.', true);
    setSaving(true);
    try {
      const res = await fetch('/api/diAdmin/periods', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newPeriod, is_active: 0 }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flash('Period created (inactive). Activate it when ready.');
      setNewPeriod({ period: '', label: '', description: '', start_date: '', end_date: '', gate_office_codes: [], gate_department_names: [] });
      setShowCreate(false);
      await fetchPeriods();
    } catch (err) { flash(err.message, true); }
    finally { setSaving(false); }
  }

  return (
    <>
      <TopBar />
      <div className="admin-page">
        <h2 className="admin-title">Admin Panel</h2>
        {msg && <div className={`admin-flash ${msg.isError ? 'error' : 'success'}`}>{msg.text}</div>}

        <div className="admin-tabs">
          {[{ key: 'periods', label: 'Periods' }, { key: 'client-groups', label: 'Client Groups' }, { key: 'data-refresh', label: 'Data Refresh' }, { key: 'pe-apply', label: 'PE Apply' }, { key: 'navbot', label: 'NavBot' }].map(t => (
            <button key={t.key} className={`admin-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'periods' && (
          <div className="admin-panel">
            <div className="admin-panel-head">
              <h3>Periods</h3>
              <button className="admin-btn" onClick={() => setShowCreate(true)}>+ New Period</button>
            </div>
            <p className="admin-hint">
              Multiple periods can be active at once. Each period is shown only to staff in its
              office/department gate (leave both empty = everyone), and you can hide individual
              sections per period. Closing a period locks input to it.
            </p>

            {initialLoading ? (
              <div className="admin-loading"><span className="admin-spinner" aria-hidden="true" /> Loading periods…</div>
            ) : (
            <table className="admin-table">
              <thead>
                <tr><th style={{ width: 34 }}></th><th>Period</th><th>Label</th><th>Applies to</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {periods.map(p => {
                  const expanded = editId === p.id;
                  return (
                  <Fragment key={p.id}>
                    <tr className={`admin-period-row ${expanded ? 'expanded' : ''}`} onClick={() => setEditId(expanded ? null : p.id)}>
                      <td className="admin-twisty">
                        <button type="button" className="admin-twisty-btn"
                          aria-expanded={expanded}
                          aria-label={expanded ? 'Collapse period settings' : 'Configure period'}>
                          {expanded ? '▾' : '▸'}
                        </button>
                      </td>
                      <td><code>{p.period}</code></td>
                      <td>{p.label}</td>
                      <td className="admin-gate-cell">{gateSummary(p)}</td>
                      <td>
                        <span className={`status-badge ${p.is_active ? 'open' : 'closed'}`}>
                          {p.is_active ? 'Active' : 'Closed'}
                        </span>
                      </td>
                      <td>
                        <button className={`toggle-btn ${p.is_active ? 'close' : 'open'}`}
                          onClick={(e) => { e.stopPropagation(); togglePeriod(p); }} disabled={saving}>
                          {p.is_active ? 'Close' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="admin-edit-row">
                        <td colSpan={6}>
                          <PeriodEditor
                            period={p}
                            sections={sections}
                            dims={dims}
                            onChanged={fetchPeriods}
                            onError={(t) => flash(t, true)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
            )}

            {showCreate && (
              <div className="admin-modal-overlay" onMouseDown={() => setShowCreate(false)}>
                <div className="admin-modal" onMouseDown={e => e.stopPropagation()}>
                  <div className="admin-modal-header">
                    <h4>Create New Period</h4>
                    <button className="admin-modal-close" onClick={() => setShowCreate(false)} aria-label="Close">×</button>
                  </div>
                  <div className="admin-form-row">
                    <input className="admin-input" placeholder="Code e.g. 2026-05 or 2026-05-FHP" maxLength={20}
                      value={newPeriod.period} onChange={e => setNewPeriod(p => ({ ...p, period: e.target.value }))} />
                    <input className="admin-input" placeholder="Label e.g. May 2026 — TAX"
                      value={newPeriod.label} onChange={e => setNewPeriod(p => ({ ...p, label: e.target.value }))} />
                  </div>
                  <div className="admin-form-row">
                    <input className="admin-input" type="date" title="Start date"
                      value={newPeriod.start_date} onChange={e => setNewPeriod(p => ({ ...p, start_date: e.target.value }))} />
                    <input className="admin-input" type="date" title="End date"
                      value={newPeriod.end_date} onChange={e => setNewPeriod(p => ({ ...p, end_date: e.target.value }))} />
                    <input className="admin-input" placeholder="Description (optional)"
                      value={newPeriod.description} onChange={e => setNewPeriod(p => ({ ...p, description: e.target.value }))} />
                  </div>
                  <GatePicker
                    dims={dims}
                    offices={newPeriod.gate_office_codes}
                    departments={newPeriod.gate_department_names}
                    onToggleOffice={o => setNewPeriod(p => ({ ...p, gate_office_codes: toggleInArray(p.gate_office_codes, o) }))}
                    onToggleDept={d => setNewPeriod(p => ({ ...p, gate_department_names: toggleInArray(p.gate_department_names, d) }))}
                  />
                  <div className="admin-modal-actions">
                    <button className="admin-btn admin-btn--secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                    <button className="admin-btn" onClick={createPeriod} disabled={saving}>Create (Inactive)</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'client-groups' && <ClientGroupsAdminPanel flash={flash} />}

        {tab === 'data-refresh' && <DataRefreshAdminPanel flash={flash} />}

        {tab === 'pe-apply' && <PeApplyAdminPanel flash={flash} />}

        {tab === 'navbot' && <NavbotAdminPanel flash={flash} />}
      </div>
    </>
  );
}

// Compact multi-select picklist: a trigger showing the current selection that
// opens a checkbox panel. Closes on outside-click / Escape. Empty = "all".
function MultiSelectDropdown({ options, selected, onToggle, allLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const summary = selected.length === 0
    ? allLabel
    : selected.length <= 3 ? selected.join(', ') : `${selected.length} selected`;

  return (
    <div className="admin-ms" ref={ref}>
      <button type="button" className="admin-ms-trigger" onClick={() => setOpen(o => !o)}>
        <span className="admin-ms-summary">{summary}</span>
        <span className="admin-ms-caret">▾</span>
      </button>
      {open && (
        <div className="admin-ms-panel">
          {options.length === 0
            ? <div className="admin-ms-empty">No options</div>
            : options.map(o => (
                <label key={o} className="admin-ms-option">
                  <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)} /> {o}
                </label>
              ))}
        </div>
      )}
    </div>
  );
}

// Office/department gate pickers (multi-select picklists from the live roster).
function GatePicker({ dims, offices, departments, onToggleOffice, onToggleDept }) {
  return (
    <div className="admin-gate-picker">
      <div className="admin-gate-group">
        <span className="admin-gate-label">Offices</span>
        <MultiSelectDropdown options={dims.offices || []} selected={offices}
          onToggle={onToggleOffice} allLabel="All offices" />
      </div>
      <div className="admin-gate-group">
        <span className="admin-gate-label">Departments</span>
        <MultiSelectDropdown options={dims.departments || []} selected={departments}
          onToggle={onToggleDept} allLabel="All departments" />
      </div>
    </div>
  );
}

// Per-period editor. Everything saves OPTIMISTICALLY on change — no save
// button. On a failed write we revert the local state and toast the error.
function PeriodEditor({ period, sections, dims, onChanged, onError }) {
  const { reload: reloadAppConfig } = useAuth();
  const [offices, setOffices]         = useState(parseList(period.gate_office_codes));
  const [departments, setDepartments] = useState(parseList(period.gate_department_names));
  const [startDate, setStartDate]     = useState(period.start_date ? String(period.start_date).slice(0, 10) : '');
  const [endDate, setEndDate]         = useState(period.end_date ? String(period.end_date).slice(0, 10) : '');
  const [description, setDescription] = useState(period.description || '');
  const [descCommitted, setDescCommitted] = useState(period.description || '');   // last saved value (revert target)
  const [secCfg, setSecCfg]           = useState({});   // section_id -> { visible, required, order }
  const [loading, setLoading]         = useState(true);  // per-period section settings load

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/diAdmin/period-sections/${period.id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (cancelled) return;
        const m = {};
        for (const r of rows || []) {
          let cfg = {};
          try { cfg = r.config_json ? JSON.parse(r.config_json) : {}; } catch { cfg = {}; }
          m[r.section_id] = {
            visible:  !(r.is_visible === 0 || r.is_visible === false),
            required: !(r.is_required === 0 || r.is_required === false),
            dev:      r.is_dev === 1 || r.is_dev === true,
            order:    r.sort_order,
            config:   cfg,   // per-period section augments (config_json blob)
          };
        }
        setSecCfg(m);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period.id, period.is_active]);   // re-read on activate / close — closing resets every flag server-side

  // A closed period (inactive, but active before) is read-only and every section reads as hidden.
  const closed = !!period.is_closed;
  const cfgFor = (sid) => {
    const c = secCfg[sid] || { visible: true, required: true, dev: false, order: null, config: {} };
    return closed ? { ...c, visible: false, required: false, dev: false } : c;
  };

  // Which section row's augments drilldown is open (section_id), if any.
  const [openAug, setOpenAug] = useState(null);

  // Optimistic PATCH of period fields; revert local state + toast on failure.
  async function commitPeriod(body, revert) {
    try {
      const res = await fetch(`/api/diAdmin/periods/${period.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      onChanged && onChanged();   // quiet refresh of the table's "Applies to" summary
      reloadAppConfig({ silent: true });   // gates change who sees the period
    } catch (err) { revert(); onError(err.message); }
  }

  function handleToggleOffice(o) {
    const prev = offices;
    const next = prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o];
    setOffices(next);
    commitPeriod({ gate_office_codes: next }, () => setOffices(prev));
  }
  function handleToggleDept(d) {
    const prev = departments;
    const next = prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d];
    setDepartments(next);
    commitPeriod({ gate_department_names: next }, () => setDepartments(prev));
  }
  function handleDate(field, value, setter, prevVal) {
    setter(value);
    commitPeriod({ [field]: value || null }, () => setter(prevVal));
  }
  function commitDescription() {
    if (description === descCommitted) return;
    const prev = descCommitted;
    setDescCommitted(description);
    commitPeriod({ description: description || null }, () => { setDescription(prev); setDescCommitted(prev); });
  }

  // Optimistic per-section save (visible / required / order); revert + toast on fail.
  async function saveSection(sectionId, patch) {
    const prev = cfgFor(sectionId);
    setSecCfg(p => ({ ...p, [sectionId]: { ...prev, ...patch } }));
    try {
      const body = { sectionId };
      if ('visible'  in patch) body.isVisible  = patch.visible;
      if ('required' in patch) body.isRequired = patch.required;
      if ('dev'      in patch) body.isDev      = patch.dev;
      if ('order'    in patch) body.sortOrder  = patch.order;
      const res = await fetch(`/api/diAdmin/period-sections/${period.id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      reloadAppConfig({ silent: true });   // visible / dev / order show up at once
    } catch (err) {
      // Revert only the fields this save changed, so a later edit to the same section isn't undone.
      setSecCfg(p => ({ ...p, [sectionId]: { ...(p[sectionId] || prev), ...Object.fromEntries(Object.keys(patch).map(k => [k, prev[k]])) } }));
      onError(err.message);
    }
  }

  // Optimistic save of one section augment — merges the key into the section's
  // per-period config_json blob (persisted whole) and POSTs it. Revert on fail.
  async function saveAugment(sectionId, augKey, value) {
    const prev = cfgFor(sectionId);
    const nextConfig = { ...(prev.config || {}), [augKey]: value };
    setSecCfg(p => ({ ...p, [sectionId]: { ...prev, config: nextConfig } }));
    try {
      const res = await fetch(`/api/diAdmin/period-sections/${period.id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionId, configJson: nextConfig }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      reloadAppConfig({ silent: true });   // augments are read from diConfig too
    } catch (err) {
      // Revert only this augment key (a later edit to another key stays).
      setSecCfg(p => {
        const cur = p[sectionId] || prev;
        const config = { ...(cur.config || {}) };
        if (prev.config && augKey in prev.config) config[augKey] = prev.config[augKey]; else delete config[augKey];
        return { ...p, [sectionId]: { ...cur, config } };
      });
      onError(err.message);
    }
  }

  return (
    <div className="admin-period-editor">
      <div className="admin-form-row">
        <input className="admin-input" type="date" value={startDate} title="Start date"
          onChange={e => handleDate('start_date', e.target.value, setStartDate, startDate)} />
        <input className="admin-input" type="date" value={endDate} title="End date"
          onChange={e => handleDate('end_date', e.target.value, setEndDate, endDate)} />
        <input className="admin-input" placeholder="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          onBlur={commitDescription} />
      </div>
      <GatePicker
        dims={dims}
        offices={offices}
        departments={departments}
        onToggleOffice={handleToggleOffice}
        onToggleDept={handleToggleDept}
      />

      <PhasesEditor period={period} onError={onError} />

      <div className="admin-section-visibility">
        <span className="admin-gate-label">Sections this period</span>
        {closed && (
          <div className="admin-phases-warn">
            This period is closed, so its sections are hidden and read-only. Activate it to change them — it will start with every section hidden.
          </div>
        )}
        {loading ? (
          <div className="admin-loading"><span className="admin-spinner" aria-hidden="true" /> Loading section settings…</div>
        ) : (
        <table className="admin-section-table">
          <tbody>
            {sections.map(s => {
              const c = cfgFor(s.id);
              const augments = augmentsFor(s.sectionKey);
              const augOpen = !closed && openAug === s.id;
              return (
                <Fragment key={s.id}>
                <tr>
                  <td className="admin-twisty">
                    {/* Sections that expose per-period augments get a drilldown
                        chevron here (first column); sections with none show
                        nothing. */}
                    {augments.length > 0 && !closed && (
                      <button type="button" className="admin-twisty-btn"
                        aria-expanded={augOpen}
                        aria-label={augOpen ? 'Hide augments' : 'Show augments'}
                        title="Per-period augments for this section"
                        onClick={() => setOpenAug(augOpen ? null : s.id)}>
                        {augOpen ? '▾' : '▸'}
                      </button>
                    )}
                  </td>
                  <td>{s.label}</td>
                  <td>
                    <ToggleSwitch checked={c.visible} label="Visible" disabled={closed}
                      onChange={(v) => saveSection(s.id, v ? { visible: true } : { visible: false, required: false })} />
                  </td>
                  <td>
                    {/* Required implies Visible and excludes Dev: turning it on shows the section and
                        turns Dev off; turning Visible off turns Required off. */}
                    <ToggleSwitch checked={c.required} label="Required" disabled={closed}
                      onChange={(v) => saveSection(s.id, v ? { required: true, visible: true, dev: false } : { required: false })} />
                  </td>
                  <td title="Admin-only — when on, admins can see and open this section for this period even if it's hidden from the population. Dev and Required are mutually exclusive: turning one on turns the other off.">
                    {/* Dev = admin-only visibility, INDEPENDENT of Visible/Required.
                        Turning it on lets admins see and open the section like
                        normal whether or not it's visible to the population — so
                        it stays enabled even when Visible is off (see diConfig). */}
                    <ToggleSwitch checked={c.dev} label="Dev" disabled={closed}
                      onChange={(v) => saveSection(s.id, v ? { dev: true, required: false } : { dev: false })} />
                  </td>
                  <td>
                    <input type="number" className="admin-input admin-order-input"
                      value={c.order ?? ''} disabled={closed || !c.visible}
                      placeholder={String(s.displayOrder ?? '')}
                      title="Order within this period (blank = default)"
                      onChange={e => saveSection(s.id, { order: e.target.value === '' ? null : Number(e.target.value) })} />
                  </td>
                </tr>
                {augOpen && augments.length > 0 && (
                  <tr className="admin-augment-row">
                    <td />
                    <td colSpan={5}>
                      <div className="admin-augment-panel">
                        {augments.map(a => (
                          <AugmentControl key={a.key} augment={a} config={c.config}
                            onSave={(v) => saveAugment(s.id, a.key, v)} />
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}

// Renders a single section augment in the drilldown. A 'text-list' augment is a
// comma-separated free-text field (e.g. office names); anything else is a
// boolean toggle.
function AugmentControl({ augment, config, onSave }) {
  if (augment.type === 'text-list') {
    return <AugmentTextList augment={augment} value={config ? config[augment.key] : undefined} onSave={onSave} />;
  }
  if (augment.type === 'vendor-source') {
    return <AugmentVendorSource augment={augment} value={config ? config[augment.key] : undefined} onSave={onSave} />;
  }
  return (
    <div className="admin-augment-item">
      <div className="admin-augment-meta">
        <span className="admin-augment-label">{augment.label}</span>
        {augment.description && <span className="admin-augment-desc">{augment.description}</span>}
      </div>
      {/* No label text next to the slider — the label/description already
          render to the left in .admin-augment-meta. */}
      <ToggleSwitch checked={augmentOn(config, augment.key)} onChange={onSave} />
    </div>
  );
}

// Comma-separated text augment. Edits locally; commits the parsed array on blur
// (only when changed), so saveAugment persists an array into config_json.
function AugmentTextList({ augment, value, onSave }) {
  const asText = (v) => (Array.isArray(v) ? v.join(', ') : (v || ''));
  const [text, setText] = useState(asText(value));
  useEffect(() => { setText(asText(value)); }, [value]);
  const commit = () => {
    const arr = text.split(',').map(s => s.trim()).filter(Boolean);
    const prevArr = Array.isArray(value) ? value : [];
    if (arr.join('|') !== prevArr.join('|')) onSave(arr);
  };
  return (
    <div className="admin-augment-item admin-augment-item--col">
      <div className="admin-augment-meta">
        <span className="admin-augment-label">{augment.label}</span>
        {augment.description && <span className="admin-augment-desc">{augment.description}</span>}
      </div>
      <input className="admin-input admin-augment-input" type="text"
        value={text} placeholder={augment.placeholder || ''}
        onChange={e => setText(e.target.value)} onBlur={commit} />
    </div>
  );
}

// Per-vendor data-source picker. Per vendor: a blob dropdown (which file feeds
// the vendor; blank = the default {vendor}.json) plus TWO join-key dropdowns —
// the source's key column and the PE key column it matches. Key options are the
// field names of a sample record from each side (fetched from
// /api/diContactSources), so there's nothing to type. A blob assigned to one
// vendor is disabled in the others (a single blob can't feed two vendors in the
// same period). Persists { <vendor>: { blob, key, peKey } } under the augment
// key; only non-default pieces are stored (empty vendor entry is dropped).
const VENDOR_SOURCE_VENDORS = [
  { key: 'knuula',   label: 'Knuula',   defaultKey: 'ContIndex', defaultPeKey: 'ContIndex' },
  { key: 'safesend', label: 'SafeSend', defaultKey: 'ClientID',  defaultPeKey: 'ClientCode' },
  { key: 'cch',      label: 'CCH',      defaultKey: 'ContIndex', defaultPeKey: 'ContIndex' },
];

// A stored vendor value may be a bare string (blob only, legacy) or
// { blob, key, peKey, match, sourceOfTruth }. `match` is 'exact' (default) or
// 'name'; `sourceOfTruth` makes this vendor's value win (no mismatch) per field.
function readSourceEntry(raw) {
  if (typeof raw === 'string') return { blob: raw, key: '', peKey: '', match: '', sourceOfTruth: false };
  if (raw && typeof raw === 'object') {
    return {
      blob: raw.blob || '', key: raw.key || '', peKey: raw.peKey || '',
      match: raw.match || '', sourceOfTruth: raw.sourceOfTruth === true,
    };
  }
  return { blob: '', key: '', peKey: '', match: '', sourceOfTruth: false };
}

// A <select> whose options come from sampled field names. Keeps the current
// value visible even if it isn't in the sampled list (blob changed / sample
// empty). `loading` disables it until the fields arrive.
function KeyFieldSelect({ value, defaultLabel, fields, loading, onChange }) {
  const opts = Array.from(new Set([...(fields || []), ...(value ? [value] : [])]));
  return (
    <select className="admin-input admin-vendor-source-key"
      value={value || ''} disabled={loading}
      onChange={e => onChange(e.target.value)}>
      <option value="">{loading ? 'Loading…' : `Default (${defaultLabel})`}</option>
      {opts.map(f => <option key={f} value={f}>{f}</option>)}
    </select>
  );
}

function AugmentVendorSource({ augment, value, onSave }) {
  const sources = useMemo(() => ((value && typeof value === 'object') ? value : {}), [value]);
  const [meta, setMeta] = useState(null);           // { blobs, peFields } | null (loading)
  const [err, setErr]   = useState(null);
  const [fieldsByBlob, setFieldsByBlob] = useState({});  // effectiveBlobName → string[]
  const requested = useRef({});                     // effectiveBlobName → true (fetch guard)

  const entryOf = useCallback((vk) => readSourceEntry(sources[vk]), [sources]);
  const blobOf  = (vk) => entryOf(vk).blob;
  const effectiveBlob = (vk) => entryOf(vk).blob || `${vk}.json`;

  // Blob list + PE fields (once).
  useEffect(() => {
    let alive = true;
    fetch('/api/diContactSources', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(j => { if (alive) setMeta({ blobs: Array.isArray(j.blobs) ? j.blobs : [], peFields: Array.isArray(j.peFields) ? j.peFields : [] }); })
      .catch(e => { if (alive) { setErr(e.message); setMeta({ blobs: [], peFields: [] }); } });
    return () => { alive = false; };
  }, []);

  // Load the field names for each vendor's currently-effective blob (on demand,
  // once per blob). Depends only on the effective-blob signature.
  const effSig = VENDOR_SOURCE_VENDORS.map(v => effectiveBlob(v.key)).join('|');
  useEffect(() => {
    VENDOR_SOURCE_VENDORS.forEach(v => {
      const eff = effectiveBlob(v.key);
      if (requested.current[eff]) return;
      requested.current[eff] = true;
      const raw = entryOf(v.key).blob;
      fetch(`/api/diContactSources?vendor=${encodeURIComponent(v.key)}&blob=${encodeURIComponent(raw || '')}`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then(j => setFieldsByBlob(p => ({ ...p, [eff]: Array.isArray(j.fields) ? j.fields : [] })))
        .catch(() => setFieldsByBlob(p => ({ ...p, [eff]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effSig]);

  // Union of listed blobs and any currently-chosen file, so a saved pick still
  // shows even if listing is empty (local dev) or the blob was later removed.
  const chosenVals = VENDOR_SOURCE_VENDORS.map(v => blobOf(v.key)).filter(Boolean);
  const blobOptions = Array.from(new Set([...((meta && meta.blobs) || []), ...chosenVals])).sort();

  // Merge a patch into one vendor's entry; drop the entry when it holds nothing.
  const commit = (vk, patch) => {
    const cur = entryOf(vk);
    const blob  = ((patch.blob  !== undefined ? patch.blob  : cur.blob)  || '').trim();
    const key   = ((patch.key   !== undefined ? patch.key   : cur.key)   || '').trim();
    const peKey = ((patch.peKey !== undefined ? patch.peKey : cur.peKey) || '').trim();
    const match = ((patch.match !== undefined ? patch.match : cur.match) || '').trim();
    const sot   = (patch.sourceOfTruth !== undefined ? patch.sourceOfTruth : cur.sourceOfTruth) === true;
    const next = { ...sources };
    if (!blob && !key && !peKey && (!match || match === 'exact') && !sot) {
      delete next[vk];
    } else {
      const stored = {};
      if (blob)  stored.blob = blob;
      if (key)   stored.key = key;
      if (peKey) stored.peKey = peKey;
      if (match === 'name') stored.match = match;   // only store the non-default
      if (sot)   stored.sourceOfTruth = true;
      next[vk] = stored;
    }
    onSave(next);
  };
  const takenByOther = (file, vk) =>
    VENDOR_SOURCE_VENDORS.some(v => v.key !== vk && blobOf(v.key) === file);

  return (
    <div className="admin-augment-item admin-augment-item--col">
      <div className="admin-augment-meta">
        <span className="admin-augment-label">{augment.label}</span>
        {augment.description && <span className="admin-augment-desc">{augment.description}</span>}
      </div>
      {meta === null ? (
        <div className="admin-loading"><span className="admin-spinner" aria-hidden="true" /> Loading data sources…</div>
      ) : (
        <div className="admin-vendor-sources">
          {VENDOR_SOURCE_VENDORS.map(v => {
            const entry = entryOf(v.key);
            const eff = effectiveBlob(v.key);
            const vFields = fieldsByBlob[eff];   // undefined = not loaded yet
            return (
              <div key={v.key} className="admin-vendor-source-row">
                <div className="admin-vendor-source-line">
                  <span className="admin-vendor-source-label">{v.label}</span>
                  <select className="admin-input admin-vendor-source-select"
                    value={entry.blob}
                    onChange={e => commit(v.key, { blob: e.target.value })}>
                    <option value="">Default ({v.key}.json)</option>
                    {blobOptions.map(b => (
                      <option key={b} value={b} disabled={takenByOther(b, v.key)}>
                        {b}{takenByOther(b, v.key) ? ' (in use)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-vendor-source-line admin-vendor-source-keys">
                  <select className="admin-input admin-vendor-source-mode"
                    value={entry.match || 'exact'}
                    onChange={e => commit(v.key, { match: e.target.value })}
                    title="Exact = identical values; By name = case/spacing/order-insensitive name match">
                    <option value="exact">Exact</option>
                    <option value="name">By name</option>
                  </select>
                  <span className="admin-vendor-source-sublabel">match</span>
                  <KeyFieldSelect
                    value={entry.key} defaultLabel={v.defaultKey}
                    fields={vFields} loading={vFields === undefined}
                    onChange={(k) => commit(v.key, { key: k })} />
                  <span className="admin-vendor-source-eq">= PE</span>
                  <KeyFieldSelect
                    value={entry.peKey} defaultLabel={v.defaultPeKey}
                    fields={meta.peFields} loading={false}
                    onChange={(k) => commit(v.key, { peKey: k })} />
                  <label className="admin-vendor-source-sot"
                    title="Source of Truth — when this vendor has a value for a field, that value rules and no mismatch is flagged.">
                    <input type="checkbox" checked={entry.sourceOfTruth === true}
                      onChange={e => commit(v.key, { sourceOfTruth: e.target.checked })} />
                    Source of truth
                  </label>
                </div>
              </div>
            );
          })}
          {err && <span className="admin-augment-desc admin-vendor-source-err">Couldn’t list sources: {err}</span>}
        </div>
      )}
    </div>
  );
}
